import React, { useState, useEffect, useRef } from 'react';
import './AutomaticDiarization.css';
import env from '../../utils/env';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ChatIcon from '@mui/icons-material/Chat';
import DeleteIcon from '@mui/icons-material/Delete';

// Controls component for starting/stopping recording
function Controls({ isRecording, onStart, onStop, onCaptureTab }) {
  return (
    <div className="transcription-controls">
      <button
        className={`control-button ${isRecording ? 'recording' : ''}`}
        onClick={isRecording ? onStop : onStart}
      >
        {isRecording ? 'Stop Recording' : 'Start Microphone'}
      </button>
      <h3 style={{ color: '#FFFFFF' }}>AI Responses</h3>
    </div>
  );
}

// Live captions component with conversation flow indicators
const LiveCaptions = ({ interimText, currentSpeaker, fullTranscript, conversationFlow }) => {
  const transcriptEndRef = useRef(null);

  useEffect(() => {
    if (transcriptEndRef.current) {
      transcriptEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [fullTranscript, interimText]);

  return (
    <div className="transcription-content">
      {fullTranscript.map((entry, index) => (
        <div key={index} style={{ marginBottom: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <strong>{entry.speaker}:</strong>
            {entry.isCompleteTurn && (
              <span style={{ 
                color: '#4CAF50', 
                fontSize: '12px',
                backgroundColor: 'rgba(76, 175, 80, 0.2)',
                padding: '2px 6px',
                borderRadius: '10px'
              }}>
                ✓ Complete
              </span>
            )}
          </div>
          <div>{entry.text}</div>
        </div>
      ))}
      {interimText && (
        <div style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <strong>{currentSpeaker}:</strong>
            {conversationFlow.turnInProgress && (
              <span style={{
                color: '#FF9800',
                fontSize: '12px',
                backgroundColor: 'rgba(255, 152, 0, 0.2)',
                padding: '2px 6px',
                borderRadius: '10px'
              }}>
                🎤 Speaking... ({Math.round(conversationFlow.confidenceLevel * 100)}%)
              </span>
            )}
          </div>
          <div>{interimText}</div>
        </div>
      )}
      <div ref={transcriptEndRef} />
    </div>
  );
};

// Transcript component
function Transcript({ transcriptData }) {
  const transcriptRef = useRef(null);
  useEffect(() => {
    // Auto-scroll to bottom when new entries are added
    if (transcriptRef.current) {
      transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
    }
  }, [transcriptData]);

  // eslint-disable-next-line
  const downloadTranscript = () => {
    let content = "Conversation Transcript:\n\n";
    transcriptData.forEach(entry => {
      content += `[${entry.timestamp.toLocaleTimeString()}] ${entry.speaker}: ${entry.text}\n`;
    });
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'transcript.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="transcript-container">
      {/* <div className="transcript-header">
        <h2>Full Transcript</h2>
        {transcriptData.length > 0 && (
          <button onClick={downloadTranscript} className="download-button">
            Download Transcript
          </button>
        )}
      </div> */}
      <div className="transcript-content" ref={transcriptRef}>
        {transcriptData.length === 0 && <p className="empty-state">No speech detected yet.</p>}
        <div className="transcript-entries">
          {[...transcriptData].reverse().map((entry, index) => (
            <div
              key={index}
              className={`transcript-entry ${entry.speaker.toLowerCase().replace(' ', '-')}`}
            >
              <div className="entry-header">
                <span className="timestamp">{entry.timestamp.toLocaleTimeString()}</span>
                <span className="speaker">{entry.speaker}</span>
              </div>
              <div className="entry-text">{entry.text}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Main component
function AutomaticDiarization() {
  const [isRecording, setIsRecording] = useState(false);
  const isRecordingRef = useRef(false);
  const [isCapturingTab, setIsCapturingTab] = useState(false);
  const [currentSpeaker, setCurrentSpeaker] = useState('Interviewer');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [fullTranscript, setFullTranscript] = useState([]);
  const [gptResponse, setGptResponse] = useState('');
  const [isSending, setIsSending] = useState(false);
  const lastSentIndexRef = useRef(0);
  const gptRequestInProgress = useRef(false);
  const [currentSessionConfig, setCurrentSessionConfig] = useState({
    role: 'Software Engineer',
    skills: ['JavaScript', 'React', 'Node.js', 'asp.net core', 'AWS', 'Azure'],
    interviewType: 'technical',
    jobTitle: '',
    jobDescription: ''
  });
  const [question, setQuestion] = useState('');
  const [conversationFlow, setConversationFlow] = useState({
    lastSpeaker: null,
    turnInProgress: false,
    silenceStartTime: null,
    confidenceLevel: 0
  });

  // Conversation flow configuration
  const turnDetectionConfig = {
    end_of_turn_confidence_threshold: 0.8,
    min_end_of_turn_silence_when_confident: 500, // ms
    max_turn_silence: 1000, // ms
    format_turns: true
  };

  // Refs
  const recognitionRef = useRef(null);
  const audioContextRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const pendingTranscriptRef = useRef('');

  // Initialize speech recognition
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert('Speech Recognition API not supported in this browser. Try Chrome or Edge.');
      return;
    }

    // Clean up function
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(track => track.stop());
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
      }
    };
  }, []);

  // Start microphone recording
  const startRecording = async () => {
    try {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';

      // Set up audio context for voice analysis
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }

      // Get microphone stream
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      // Set default speaker to Candidate for microphone
      setCurrentSpeaker('Candidate');

      // Set up recognition handlers
      setupRecognitionHandlers();

      // Start recognition
      recognitionRef.current.start();
      isRecordingRef.current = true; // <-- Set ref to true
      setIsRecording(true);
      setIsCapturingTab(false);

    } catch (error) {
      console.error("Error starting recording:", error);
      alert("Failed to start recording: " + error.message);
    }
  };

  // Start tab audio capture
  const startTabCapture = async () => {
    try {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';

      // Request screen sharing with audio
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: true
      });

      // Check if audio is included
      const audioTracks = stream.getAudioTracks();
      if (audioTracks.length === 0) {
        alert("No audio track found. Please make sure to select 'Share audio' when sharing the tab.");
        stream.getTracks().forEach(track => track.stop());
        return;
      }

      mediaStreamRef.current = stream;

      // Set up audio context
      if (!audioContextRef.current) {
        // eslint-disable-next-line
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }

      // Set default speaker to Interviewer for tab audio
      setCurrentSpeaker('Interviewer');

      // Set up recognition handlers
      setupRecognitionHandlers();

      // Start recognition
      recognitionRef.current.start();
      isRecordingRef.current = true; // <-- Set ref to true      
      setIsRecording(true);
      setIsCapturingTab(true);

      // Handle stream ending
      stream.getVideoTracks()[0].onended = () => {
        stopRecording();
      };

    } catch (error) {
      console.error("Error capturing tab audio:", error);
      alert("Failed to capture tab audio: " + error.message);
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }

    // Clear any pending silence timer
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }

    isRecordingRef.current = false; // <-- Set ref to false    
    setIsRecording(false);
    setIsCapturingTab(false);

    // Process any remaining transcript as a complete turn
    const remainingText = pendingTranscriptRef.current || interimTranscript.trim();
    if (remainingText) {
      setFullTranscript(prev => [
        ...prev,
        {
          speaker: currentSpeaker,
          text: remainingText,
          timestamp: new Date(),
          isCompleteTurn: true
        }
      ]);
      setInterimTranscript('');
      pendingTranscriptRef.current = '';
    }

    // Reset conversation flow
    setConversationFlow({
      lastSpeaker: currentSpeaker,
      turnInProgress: false,
      silenceStartTime: null,
      confidenceLevel: 0
    });
  };

  // Detect turn changes and manage conversation flow
  const detectTurnChange = (transcript, confidence) => {
    
    // Clear any existing silence timer
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }

    // Update conversation flow state
    setConversationFlow(prev => ({
      ...prev,
      turnInProgress: true,
      confidenceLevel: confidence,
      silenceStartTime: null
    }));

    // Smart speaker detection based on content patterns
    const isQuestion = /[?]$/.test(transcript.trim()) || 
                     /^(what|how|why|when|where|who|can|could|would|should|do|does|did|is|are|was|were|will|tell me|explain)/i.test(transcript.trim());
    
    const isInterviewerPhrase = /^(let's|so|okay|alright|next|now|tell me|explain|can you|describe|what|how)/i.test(transcript.trim());
    
    // Auto-switch speaker based on content
    if (isQuestion || isInterviewerPhrase) {
      if (currentSpeaker !== 'Interviewer') {
        setCurrentSpeaker('Interviewer');
      }
    } else if (currentSpeaker !== 'Candidate') {
      setCurrentSpeaker('Candidate');
    }
  };

  // Handle end of turn detection
  const handleEndOfTurn = () => {
    const pendingText = pendingTranscriptRef.current;
    if (!pendingText.trim()) return;

    // Add complete turn to transcript
    setFullTranscript(prev => [
      ...prev,
      {
        speaker: currentSpeaker,
        text: pendingText.trim(),
        timestamp: new Date(),
        isCompleteTurn: true
      }
    ]);

    // Clear pending transcript
    pendingTranscriptRef.current = '';
    setInterimTranscript('');

    // Update conversation flow
    setConversationFlow(prev => ({
      ...prev,
      lastSpeaker: currentSpeaker,
      turnInProgress: false,
      silenceStartTime: null
    }));
  };

  // Set up recognition event handlers
  const setupRecognitionHandlers = () => {
    if (!recognitionRef.current) return;

    recognitionRef.current.onstart = () => {
      setIsRecording(true);
    };

    recognitionRef.current.onresult = (event) => {
      let interim = '';
      let final = '';
      let maxConfidence = 0;

      for (let i = event.resultIndex; i < event.results.length; ++i) {
        const result = event.results[i];
        const confidence = result[0].confidence || 0.5;
        maxConfidence = Math.max(maxConfidence, confidence);

        if (result.isFinal) {
          final += result[0].transcript;
        } else {
          interim += result[0].transcript;
        }
      }

      // Handle interim results
      if (interim) {
        setInterimTranscript(interim);
        pendingTranscriptRef.current = interim;
        detectTurnChange(interim, maxConfidence);
      }

      // Handle final results with turn detection
      if (final) {
        pendingTranscriptRef.current = final;
        detectTurnChange(final, maxConfidence);

        // Start silence detection for end of turn
        const requiredSilence = maxConfidence >= turnDetectionConfig.end_of_turn_confidence_threshold
          ? turnDetectionConfig.min_end_of_turn_silence_when_confident
          : turnDetectionConfig.max_turn_silence;

        silenceTimerRef.current = setTimeout(() => {
          handleEndOfTurn();
        }, requiredSilence);
      }
    };

    recognitionRef.current.onerror = (event) => {
      console.error("Recognition error:", event.error);
      if (event.error !== 'no-speech') {
        stopRecording();
      }
    };

    recognitionRef.current.onend = () => {
      // setIsRecording(false);
      // Restart if we're still supposed to be recording
      if (isRecording) {
        try {
          console.log('restart recognition..');
          recognitionRef.current.start();
        } catch (error) {
          console.error("Failed to restart recognition:", error);
        }
      } else {
        setIsRecording(false);
      }
    };
  };

  // Send only new transcript entries to GPT
  const sendToGPT = async (newEntries) => {
    if (gptRequestInProgress.current) return;
    gptRequestInProgress.current = true;
    const apiKey = env.OPENAI_API_KEY;
    if (!apiKey || !newEntries.length) {
      gptRequestInProgress.current = false;
      return;
    }
    setIsSending(true);
    try {
      const transcriptText = newEntries.map(entry => `${entry.speaker}: ${entry.text}`).join('\n');
      // const systemPrompt = `You are an expert AI interviewer. Here are new lines from a live interview transcript. Only answer or summarize based on these new lines. Ignore any previous context or script. If a question is present, answer it. If not, provide a brief summary or feedback.`;
      const systemPrompt = `
        Role/Persona: You are a candidate applying for a ${currentSessionConfig?.jobTitle || 'Senior Software Engineer'} position. You are confident, experienced, and articulate in responding to interview questions.
            
            Your Background:
            - Skills: ${currentSessionConfig?.skills?.join(', ') || 'JavaScript, React, Node.js, ASP.NET Core, AWS, Azure'}
            - Interview Type: ${currentSessionConfig?.interviewType || 'Technical'} interview
            ${currentSessionConfig?.jobDescription ? `- Target Role: ${currentSessionConfig.jobDescription}` : ''}
            
            Instructions:
            - Only respond if the interviewer asks a direct question or requests information
            - Provide clear, concise answers in 2-4 sentences
            - Include technical depth and real-life examples when relevant
            - Be honest if you don't know something - say so professionally
            - Show enthusiasm and confidence in your responses
            - Do not ask questions back unless specifically prompted
            - Do not respond to casual conversation or statements that aren't questions
            - Focus only on answering what was asked
            
            Style: Professional, confident, and articulate. Demonstrate your expertise while being genuine.
            
            Latest conversation:
            `;
      const response = await fetch("https://api.openai.com/v1/chat/completions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          model: "gpt-4.1-nano",
          messages: [
            { role: "system", content: systemPrompt },
            { role: "user", content: transcriptText }
          ]
        })
      });
      const data = await response.json();
      const newGptText = data.choices?.[0]?.message?.content || '';
      setGptResponse(prev => prev ? prev + '\n---\n' + newGptText : newGptText);
      lastSentIndexRef.current += newEntries.length;
    } catch (error) {
      setGptResponse(prev => prev + '\n[Error: ' + error.message + ']');
    } finally {
      setIsSending(false);
      gptRequestInProgress.current = false;
    }
  };

  // Only send complete turns to GPT
  useEffect(() => {
    if (!isRecording) return;
    if (fullTranscript.length > lastSentIndexRef.current && !gptRequestInProgress.current) {
      const newEntries = fullTranscript.slice(lastSentIndexRef.current);
      // Only send entries that are marked as complete turns
      const completeTurns = newEntries.filter(entry => entry.isCompleteTurn);
      
      if (completeTurns.length > 0) {
        sendToGPT(completeTurns);
      }
    }
  }, [fullTranscript, isRecording]);

  // Split gptResponse into array of responses (descending order)
  const gptResponses = gptResponse
    ? gptResponse.split(/\n---\n/).filter(Boolean).reverse()
    : [];

  const handleClearTranscript = () => {
    setFullTranscript([]);
    setInterimTranscript('');
  };

  const handleSendQuestion = () => {
    if (!question.trim()) return;
    
    // Add user question to transcript
    setFullTranscript(prev => [
      ...prev,
      {
        speaker: 'You',
        text: question.trim(),
        timestamp: new Date()
      }
    ]);
    
    // Process with GPT
    sendToGPT([{
      speaker: 'You',
      text: question.trim(),
      timestamp: new Date()
    }]);
    
    // Clear question input
    setQuestion('');
  };

  return (
    <div className="automatic-diarization">
      <div className="main-content">
        <div className="livecaptions-section">
          <div className="livecaptions-header">
            <h3><ChatIcon className="mic-icon" /> Live Transcription</h3>
            <button className="clear-button" onClick={handleClearTranscript}>
              <DeleteIcon fontSize="small" /> Clear
            </button>
          </div>
          
          <button 
            className={`mic-button ${isRecording ? 'recording' : ''}`}
            onClick={isRecording ? stopRecording : startRecording}
          >
            {isRecording ? <MicOffIcon /> : <MicIcon />}
          </button>
          
          {fullTranscript.length === 0 && !interimTranscript && (
            <div className="transcription-placeholder">
              {isRecording 
                ? "Your speech will appear here in real-time..." 
                : "Click the microphone button to start recording"}
            </div>
          )}
          
          {(fullTranscript.length > 0 || interimTranscript) && (
            <LiveCaptions 
              interimText={interimTranscript}
              currentSpeaker={currentSpeaker}
              fullTranscript={fullTranscript}
              conversationFlow={conversationFlow}
            />
          )}
        </div>
        
        <div className="ai-response-section">
          <div className="ai-response-header">
            <h3><SmartToyIcon /> AI Interview Chat</h3>
          </div>
          
          <div className="ai-chat-input">
            <input
              type="text"
              placeholder="Type your message or question..."
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSendQuestion()}
            />
            <button onClick={handleSendQuestion}>
              <SendIcon fontSize="small" />
            </button>
          </div>
          
          <div className="ai-responses">
            {gptResponses.length === 0 ? (
              <div className="response-placeholder">
                Start speaking or type a message to begin the interview...
              </div>
            ) : (
              gptResponses.map((resp, idx) => (
                <div className="gpt-response-card" key={idx}>
                  {resp}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      
      <button
        className="send-button"
        onClick={() => {
          const newEntries = fullTranscript.slice(lastSentIndexRef.current);
          sendToGPT(newEntries);
        }}
        disabled={isSending || fullTranscript.length === 0}
      >
        {/* {isSending ? 'Processing...' : 'Send'} */}
        {!isSending && <SendIcon fontSize="small" />}
      </button>
    </div>
  );
}

export default AutomaticDiarization;
